import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Clock3 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { MaskedLine, Reveal } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import SEO from "@/components/SEO";
import { organizationJsonLd } from "@/components/SEO";
import { HERO_IMAGES } from "@/lib/constants";

const STATS = [
  { value: "50+", label: "Firme partenere", testid: "hero-stat-partners" },
  { value: "30+", label: "Furnizori de materiale", testid: "hero-stat-suppliers" },
  { value: "1", label: "Singur punct de contact", testid: "hero-stat-contact" },
];

export default function Hero() {
  const { openQuote } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} className="grain relative flex min-h-[100svh] flex-col overflow-hidden bg-ink-950" data-testid="hero-section">
      <SEO jsonLd={organizationJsonLd} path="/" />
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0" initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1.05, opacity: 1 }} transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}>
        <img
          src={HERO_IMAGES.background}
          alt="Șantier de construcții la apus — echipe Brox Construct la lucru"
          className="h-full w-full object-cover"
          fetchPriority="high"
          data-testid="hero-bg-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/70 to-ink-950/30" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-ink-950/60" aria-hidden="true" />

      <div className="container-brox relative flex flex-1 flex-col justify-center pt-24 pb-12">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          BROX CONSTRUCT — PUNCT UNIC DE CONTACT
        </motion.p>

        <h1 className="display-title text-[17vw] leading-[0.92] sm:text-7xl lg:text-8xl xl:text-9xl" data-testid="hero-headline">
          <MaskedLine index={0}>Ca la noi,</MaskedLine>
          <MaskedLine index={1}>
            <span className="text-copper">la nimeni.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="mt-6 flex items-center gap-3 font-display text-xl font-bold uppercase tracking-wide text-paper sm:text-2xl lg:text-3xl"
          data-testid="hero-promise"
        >
          <span className="inline-block h-7 w-1 shrink-0 bg-copper" aria-hidden="true" />
          Un singur contact. Tot proiectul.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-paper/80 sm:text-lg"
          data-testid="hero-subline"
        >
          De la renovări și instalații până la construcții complete. Ne spui ce ai nevoie.
          Noi organizăm oamenii, materialele și execuția.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row sm:gap-4"
          data-testid="hero-ctas"
        >
          <button onClick={() => openQuote({ source: "hero" })} className="btn-primary copper-sheen" data-testid="hero-cta-whatsapp">
            <WhatsAppIcon className="h-5 w-5" />
            CERE OFERTĂ PE WHATSAPP
          </button>
          <Link to="/servicii" className="btn-ghost" data-testid="hero-cta-services">
            VEZI SERVICIILE
            <ArrowDown className="h-5 w-5" />
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-5 flex items-center gap-2 text-xs text-paper/60 sm:text-sm"
          data-testid="hero-microcopy"
        >
          <Clock3 className="h-4 w-4 text-copper" />
          Trimite-ne proiectul tău. Te contactăm în maximum 24 de ore.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 0.8 }}
        className="relative border-t border-white/10 bg-ink-950/60 backdrop-blur-md"
        data-testid="hero-stats"
      >
        <div className="container-brox grid grid-cols-3 divide-x divide-white/10">
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col items-start gap-1 px-3 py-4 sm:px-6 sm:py-7" data-testid={s.testid}>
              <span className="font-display text-3xl font-extrabold text-copper sm:text-5xl">{s.value}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-paper/70 sm:text-[11px] sm:tracking-[0.2em]">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
